import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';

const sections = [
  {
    number: '1',
    title: 'Key Definitions',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <span className="font-medium text-foreground">Personal Data:</span> Information that
            identifies or can reasonably be linked to an identifiable person.
          </li>
          <li>
            <span className="font-medium text-foreground">Case Materials:</span> Content you
            submit for analysis (images, PDFs, field notes, metadata, Q&amp;A responses, and
            related information).
          </li>
          <li>
            <span className="font-medium text-foreground">Expert Content:</span> Expert profile
            data, credentials, communications, and deliverables submitted through the
            marketplace.
          </li>
          <li>
            <span className="font-medium text-foreground">Processing:</span> Collecting, storing,
            using, disclosing, analyzing, deleting, or otherwise handling data.
          </li>
          <li>
            <span className="font-medium text-foreground">Sensitive Data:</span> Social Security
            numbers, government ID numbers, full payment card numbers, bank account credentials,
            health/medical information, precise geolocation, biometric identifiers, data about
            minors, or other information legally defined as "sensitive" in your jurisdiction.
          </li>
        </ul>
      </div>
    ),
  },
  {
    number: '2',
    title: 'Information We Collect',
    content: (
      <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
        <div>
          <p className="font-medium text-foreground mb-1">A. Information You Provide</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <span className="font-medium text-foreground">Account &amp; Profile:</span> Name,
              email, password, organization/company (optional), role/persona selection, and
              contact details you choose to provide.
            </li>
            <li>
              <span className="font-medium text-foreground">
                Case Materials (Customers):
              </span>{' '}
              Photos/images, documents/PDFs (if enabled), field notes, case context, incident
              details, and responses to clarifying questions.
            </li>
            <li>
              <span className="font-medium text-foreground">
                Expert Profiles &amp; Credentials (Experts):
              </span>{' '}
              Bio, specialties, jurisdictions, rate, certifications/licenses (as provided), and
              credential files for review/approval.
            </li>
            <li>
              <span className="font-medium text-foreground">
                Marketplace &amp; Communications:
              </span>{' '}
              In-app messages (where enabled), contract data (offers/acceptance), and work logs
              (milestones/time entries) for marketplace engagements.
            </li>
            <li>
              <span className="font-medium text-foreground">Support:</span> Messages you send to
              support, including attachments you choose to provide.
            </li>
          </ul>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">
            B. Information Collected Automatically
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              IP address, device and browser info, timestamps, log data, and usage events (e.g.,
              pages viewed, actions taken).
            </li>
            <li>
              Security/audit events related to authentication, downloads, and administrative
              actions.
            </li>
          </ul>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">C. Payment Information</p>
          <p>
            Payments are processed by Stripe (or another payment processor we designate). We do
            not store full card numbers on our servers. We may receive limited billing details
            such as billing name/email, last4, card brand, payment status, invoice metadata, and
            payment/dispute status.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">
            D. Identity/Tax Onboarding (Experts)
          </p>
          <p>
            If you use marketplace features as an Expert, Stripe or other providers may collect
            identity verification and tax-related information (e.g., W-9/W-8) to enable payouts
            and comply with applicable law. We may receive limited status or metadata indicating
            onboarding completion or issues.
          </p>
        </div>
      </div>
    ),
  },
  {
    number: '3',
    title: 'How We Use Information',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>We use information to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            Provide, operate, and maintain the Service (accounts, case intake, report
            generation, downloads).
          </li>
          <li>
            Perform AI-assisted analysis and generate reports and image annotations.
          </li>
          <li>Ask clarifying questions to improve report completeness.</li>
          <li>
            Facilitate the Expert marketplace (matching, contracts, escrow/payment holds,
            messaging, dispute handling, and enforcement).
          </li>
          <li>
            Prevent fraud and enforce our policies (including detecting attempts to move
            communication or payment off-platform).
          </li>
          <li>
            Communicate with you (verification, security alerts, transactional emails,
            support).
          </li>
          <li>Monitor performance, debug issues, and improve the Service.</li>
          <li>
            Comply with legal obligations and protect the rights and safety of users and the
            Service.
          </li>
        </ul>
      </div>
    ),
  },
  {
    number: '4',
    title: 'AI Processing (Important)',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          FireCaseIQ uses AI models (including OpenAI's ChatGPT API) to process Case Materials
          and generate preliminary analysis and report text. The AI output is preliminary and
          informational and may be inaccurate or incomplete. The Service does not generate
          images. It only annotates user-uploaded images. If something is not visible/provided,
          the Service should state "Not observable / Not provided" rather than invent details.
        </p>
        <div>
          <p className="font-medium text-foreground mb-1">Model Improvement / Training</p>
          <p>
            We operate the Service on a paid hosting/deployment tier and configure the Service
            to avoid using Customer Case Materials for unrelated third-party training where
            provider settings and contracts permit. Third-party providers may still process data
            to provide services to us, and their handling is governed by their own terms and
            privacy policies.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">Do Not Upload Sensitive Data</p>
          <p>
            Do not upload Sensitive Data unless strictly necessary. When possible, redact
            Sensitive Data from Case Materials and communications. Case Materials may contain
            personal information; you are responsible for ensuring you have the right to submit
            and share any information you upload.
          </p>
        </div>
      </div>
    ),
  },
  {
    number: '5',
    title: 'How We Share Information',
    content: (
      <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
        <div>
          <p className="font-medium text-foreground mb-1">
            A. Service Providers (Subprocessors)
          </p>
          <p>
            We use third parties to run the Service. Depending on your use, they may process
            Personal Data and/or Case Materials on our behalf, such as:
          </p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>
              <span className="font-medium text-foreground">OpenAI (ChatGPT API):</span> AI
              analysis and report drafting based on inputs you submit
            </li>
            <li>
              <span className="font-medium text-foreground">Supabase:</span> authentication,
              database, and file storage
            </li>
            <li>
              <span className="font-medium text-foreground">Stripe:</span> payments,
              subscriptions, invoicing, fraud tooling, and escrow/payment flows;
              identity/tax onboarding for payouts
            </li>
            <li>
              <span className="font-medium text-foreground">Resend:</span> transactional email
              delivery (verification, receipts, notifications)
            </li>
            <li>
              <span className="font-medium text-foreground">Vercel:</span>{' '}
              hosting/deployment platform and operational infrastructure/logs supporting the
              Service
            </li>
          </ul>
          <p className="mt-2">We may also use, if enabled:</p>
          <ul className="list-disc pl-5 space-y-1 mt-1">
            <li>Analytics tools (e.g., Google Analytics, PostHog, or similar)</li>
            <li>Error/performance monitoring (e.g., Sentry or similar)</li>
            <li>Anti-abuse/bot protection (e.g., CAPTCHA providers)</li>
            <li>Customer support tools (e.g., Intercom/Zendesk or similar)</li>
          </ul>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">B. Expert Marketplace Sharing</p>
          <p>
            If you engage an Expert, we may share relevant Case Materials with that Expert only
            to the extent needed for the engagement. Expert identifying information may be
            limited or masked until certain steps occur (e.g., contract acceptance and/or
            payment authorization), depending on platform rules.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">
            C. Legal, Safety, and Enforcement
          </p>
          <p>
            We may disclose information if we believe it is necessary to comply with law,
            respond to lawful requests, protect rights/safety/security, investigate fraud/abuse,
            or enforce our agreements.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">D. Business Transfers</p>
          <p>
            If we undergo a merger, acquisition, financing, reorganization, or sale of assets,
            information may be transferred as part of that transaction.
          </p>
        </div>
      </div>
    ),
  },
  {
    number: '6',
    title: 'Data Retention',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          We retain information as needed to provide the Service, maintain billing and dispute
          records, comply with legal obligations, and protect the Service. Retention may vary by
          plan. You may request deletion subject to legal and operational requirements.
        </p>
      </div>
    ),
  },
  {
    number: '7',
    title: 'Security',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          We use administrative, technical, and organizational measures designed to help protect
          information, such as TLS/HTTPS in transit, encryption at rest where supported, access
          controls, role-based permissions, 2FA features (where enabled), and audit logging.
          However, no method of transmission or storage is completely secure, and we cannot
          guarantee absolute security.
        </p>
      </div>
    ),
  },
  {
    number: '8',
    title: 'Your Choices and Rights',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          You may update your profile, manage security settings, and opt out of marketing emails
          (transactional/security emails may still be sent). Where applicable, you may request
          access, correction, deletion, or portability of certain Personal Data, or object to
          certain processing. To submit a request, contact us at{' '}
          <a href="mailto:info@firecaseiq.com" className="text-primary hover:underline">
            info@firecaseiq.com
          </a>
          .
        </p>
      </div>
    ),
  },
  {
    number: '9',
    title: 'US State Privacy Disclosures',
    content: (
      <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
        <p>
          Depending on where you live, you may have additional rights under US state privacy laws
          (which may include rights to access, delete, correct, obtain a copy of your data, and
          opt out of certain processing such as targeted advertising or certain profiling, where
          applicable).
        </p>
        <div>
          <p className="font-medium text-foreground mb-1">
            A. Categories of Personal Data (General)
          </p>
          <p>
            We may collect categories of Personal Data such as identifiers (name, email),
            account and profile information, commercial information (transactions),
            internet/network activity (logs and usage data), and (for Experts) professional or
            credential information. Case Materials may include personal information depending on
            what you upload.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">"Sale" / "Sharing"</p>
          <p>
            We do not sell Personal Data. We do not share Personal Data for cross-context
            behavioral advertising as those terms may be defined under certain laws.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">C. How to Submit Requests</p>
          <p>
            Email{' '}
            <a href="mailto:info@firecaseiq.com" className="text-primary hover:underline">
              info@firecaseiq.com
            </a>{' '}
            with "Privacy Request" in the subject line. We may verify your identity and/or
            authority (e.g., authorized agent) before fulfilling a request. We will respond
            within legally required timeframes.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">D. Appeals</p>
          <p>
            Where required by law, you may appeal a decision regarding your request by replying
            to our response and stating you wish to appeal.
          </p>
        </div>
      </div>
    ),
  },
  {
    number: '10',
    title: 'Children',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          The Service is not intended for individuals under 18. We do not knowingly collect
          Personal Data from minors.
        </p>
      </div>
    ),
  },
  {
    number: '11',
    title: 'International Use',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          We are US-based. If you access the Service from outside the US, your data may be
          processed in the US. You are responsible for complying with local laws applicable to
          your use and for ensuring you have the right to transfer and submit any data you
          provide.
        </p>
      </div>
    ),
  },
  {
    number: '12',
    title: 'Changes',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          We may update this Privacy Policy and will revise the "Last Updated" date.
        </p>
      </div>
    ),
  },
  {
    number: '13',
    title: 'Contact',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <div className="space-y-1">
          <p className="font-medium text-foreground">Filmology LLC d/b/a Grunt Logic</p>
          <p>Attn: Privacy</p>
          <p>5448 Apex Peakway Suite 157</p>
          <p>Apex, NC 27502</p>
        </div>
        <p>
          Email:{' '}
          <a href="mailto:info@firecaseiq.com" className="text-primary hover:underline">
            info@firecaseiq.com
          </a>
        </p>
      </div>
    ),
  },
];

const AccordionItem = ({ section, isOpen, onToggle }) => (
  <div className="border border-border rounded-lg overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-5 py-4 bg-card hover:bg-muted/50 transition-colors text-left group"
    >
      <div className="flex items-center gap-3">
        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
          {section.number}
        </span>
        <span className="font-semibold text-foreground text-sm">{section.title}</span>
      </div>
      <Icon
        name={isOpen ? 'ChevronUp' : 'ChevronDown'}
        size={18}
        className="text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0 ml-3"
      />
    </button>
    {isOpen && (
      <div className="px-5 py-4 bg-background border-t border-border">
        {section.content}
      </div>
    )}
  </div>
);

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border py-4">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center">
            <img src="/assets/images/logo.png" alt="FireCaseIQ" className="h-10 object-contain" />
          </button>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="ArrowLeft" size={20} />
            Back
          </button>
        </div>
      </header>

      {/* Page content */}
      <main className="max-w-3xl mx-auto px-4 md:px-6 py-10 space-y-8">
        {/* Title block */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-foreground">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground">
            <span className="font-medium">Effective Date:</span> February 15, 2026
            &nbsp;&nbsp;|&nbsp;&nbsp;
            <span className="font-medium">Last Updated:</span> February 27, 2026
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed pt-1">
            This Privacy Policy explains how Filmology LLC d/b/a Grunt Logic ("Grunt Logic,"
            "we," "us," or "our") collects, uses, discloses, and protects information when you
            access or use FireCaseIQ (the "Service"), including our website, applications, and
            related services. This Privacy Policy applies to: Customers (users who submit case
            materials and receive AI-generated reports), and Experts (independent professionals
            who offer services through the Service's marketplace features).
          </p>
        </div>

        {/* Accordion sections */}
        <div className="space-y-3">
          {sections.map((section, i) => (
            <AccordionItem
              key={section.number}
              section={section}
              isOpen={openIndex === i}
              onToggle={() => toggle(i)}
            />
          ))}
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicyPage;
